import React from 'react';
import { Trans } from 'react-i18next';
import ThemeConstants from '../../config/theme/theme_constants';
import { useTranslation } from '../../i18n/useTranslation';
import DocumentBackButton from './document_back_button';

const theme = ThemeConstants();

const CGUUvibes: React.FC = () => {
  return (
    <div className='flex flex-col min-h-dvh w-full relative' style={{ background: `linear-gradient(90deg, ${theme.UvibesPinkGradient} 0%, ${theme.UvibesOrangeGradient} 75%)` }}>
      <DocumentBackButton />
      <Header />
      <div className="rounded-t-[30px] bg-white px-4 py-8 w-full space-y-12">
        <Preambule />
        <AcceptationCGU />
        <DescriptionService />
        <InscriptionAcces />
        <ComportementAttendu />
        <ConsequencesJuridiques />
        <ConfidentialiteAdministrateur />
        <ConfidentialiteDonnees />
        <Decharge/>
        <Responsabilite />
        <ModificationsCGU />
        <Resiliation />
        <DroitApplicable />
        <Footer />
      </div>
    </div>
  );
};

const Header: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="text-white p-12 text-center">
      <div className="space-y-6">
        <div className="text-5xl">⚖️</div>
        <h1 className="text-4xl font-bold leading-tight">
          {t('terms.title')}
        </h1>
        <p className="text-xl text-white/90">
          {t('terms.subtitle')}
        </p>
        <div className="w-24 h-1 bg-white/60 mx-auto rounded-full"></div>
      </div>
    </div>
  );
};

const Preambule: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle title={t('terms.preamble.title')} emoji="📋" />
      <div className="border-l-4 p-6 rounded-r-lg" style={{ borderColor: theme.UvibesPink, backgroundColor: theme.UvibesPink + '20' }}>
        <div className="space-y-4 text-gray-700 leading-relaxed">
          <p>
            <Trans
              i18nKey="terms.preamble.p1"
              components={{
                term: <span className="font-semibold" />,
                brand: <span className="font-semibold" style={{ color: theme.UvibesPink }} />,
              }}
            />
          </p>
          <p className="font-medium" style={{ color: theme.UvibesPink }}>
            <Trans
              i18nKey="terms.preamble.p2"
              components={{ term: <span className="font-semibold" /> }}
            />
          </p>
        </div>
      </div>
    </section>
  );
};

const AcceptationCGU: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle number="1" title={t('terms.acceptance.title')} emoji="✅" />
      <div className="text-gray-700 leading-relaxed">
        <p>
          {t('terms.acceptance.p1')}
        </p>
      </div>
    </section>
  );
};

const DescriptionService: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle number="2" title={t('terms.service.title')} emoji="🔄" />
      <div className="text-gray-700 leading-relaxed">
        <p>
          {t('terms.service.p1')}
        </p>
      </div>
    </section>
  );
};

const InscriptionAcces: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle number="3" title={t('terms.registration.title')} emoji="🔐" />
      <div className="text-gray-700 leading-relaxed space-y-4">
        <p>
          {t('terms.registration.p1')}
        </p>

        <p>
          {t('terms.registration.p2')}
        </p>
      </div>
    </section>
  );
};

const ComportementAttendu: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle number="4" title={t('terms.behavior.title')} emoji="🤝" />
      <div className="space-y-6 text-gray-700 leading-relaxed">
        <p>
          {t('terms.behavior.intro')}
        </p>

        <div className="border-l-4 p-6 rounded-r-lg" style={{ borderColor: theme.UvibesRed, backgroundColor: theme.UvibesRed + '20', color: theme.UvibesRed }}>
          <h4 className="font-semibold mb-4 flex items-center gap-2">
            {t('terms.behavior.forbidden_title')}
          </h4>

          <div className="space-y-3">
            {[
              t('terms.behavior.forbidden_1'),
              t('terms.behavior.forbidden_2'),
              t('terms.behavior.forbidden_3'),
              t('terms.behavior.forbidden_4'),
              t('terms.behavior.forbidden_5')
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-lg mt-0.5">•</span>
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>

          <div className="mt-4 p-4 bg-orange-100 rounded-lg border-l-4 border-orange-400">
            <p className="text-sm text-orange-800 font-medium">
              {t('terms.behavior.sanctions')}
            </p>
          </div>
        </div>

        <div className="border-l-4 p-6 rounded-r-lg" style={{ borderColor: theme.UvibesRed, backgroundColor: theme.UvibesRed + '20', color: theme.UvibesRed }}>
            <p className="text-sm">
              <Trans i18nKey="terms.behavior.screenshots.intro" />
            </p>

          <div className="space-y-3">
            {[
              t('terms.behavior.screenshots.rule_1'),
              t('terms.behavior.screenshots.rule_2'),
              t('terms.behavior.screenshots.rule_3'),
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-lg mt-0.5">•</span>
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>

          <div className="mt-4 p-4 bg-orange-100 rounded-lg border-l-4 border-orange-400">
            <p className="text-sm text-orange-800 font-medium">
              {t('terms.behavior.screenshots.sanctions')}
            </p>
          </div>
        </div>
      </div>

    </section>
  );
};

const ConsequencesJuridiques: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle number="5" title={t('terms.legal.title')} emoji="⚖️" />
      <div className="space-y-6 text-gray-700 leading-relaxed">
        <p>
          {t('terms.legal.intro')}
        </p>

        <div className="border-l-4 p-6 rounded-r-lg" style={{ borderColor: theme.UvibesYellow, backgroundColor: theme.UvibesYellow + '20' }}>
          <h4 className="font-semibold text-yellow-800 mb-4">
            {t('terms.legal.particular')}
          </h4>

          <div className="space-y-4">
            <ConsequenceCard
              emoji="🏛️"
              title={t('terms.legal.lawsuits.title')}
              description={t('terms.legal.lawsuits.description')}
            />

            <ConsequenceCard
              emoji="🚨"
              title={t('terms.legal.sanctions.title')}
              description={t('terms.legal.sanctions.description')}
            />

            <ConsequenceCard
              emoji="📢"
              title={t('terms.legal.report.title')}
              description={t('terms.legal.report.description')}
            />
          </div>

          <div className="mt-6 p-4 bg-red-100 rounded-lg border-2 border-red-300">
            <p className="text-sm text-red-800 font-medium">
              {t('terms.legal.cooperation')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const ConfidentialiteAdministrateur: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle number="6" title={t('terms.admin.title')} emoji="📝" />
      <div className="space-y-4 text-gray-700 leading-relaxed">
        <p>
          {t('terms.admin.p1')}
        </p>

        <p>
          {t('terms.admin.p2')}
        </p>
        <div className="ml-6 space-y-2">
          <p>{t('terms.admin.item_1')}</p>
          <p>{t('terms.admin.item_2')}</p>
        </div>

        <p>
          {t('terms.admin.p3')}
        </p>

        <p>
          {t('terms.admin.p4')}
        </p>
      </div>
    </section>
  );
};

const ConfidentialiteDonnees: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle number="7" title={t('terms.data.title')} emoji="🔒" />
      <div className="space-y-4 text-gray-700 leading-relaxed">
        <p>
          {t('terms.data.p1')}
        </p>
        <div className="border-l-4 p-4 rounded-r-lg" style={{ borderColor: theme.UvibesGreen, backgroundColor: theme.UvibesGreen + '20' }}>
          <p className='text-black'>
            {t('terms.data.rights')}
          </p>
        </div>

        <p>
          {t('terms.data.inactivity')}
        </p>
      </div>
    </section>
  );
};

const Decharge: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle number="8" title={t('terms.user_liability.title')} emoji="📝" />
      <div className="space-y-4 text-gray-700 leading-relaxed">
        <p>{t('terms.user_liability.p1')}</p>
        <p>{t('terms.user_liability.p2')}</p>
        <p>{t('terms.user_liability.p3')}</p>
      </div>
    </section>
  );
};

const Responsabilite: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle number="9" title={t('terms.liability.title')} emoji="🛡️" />
      <div className="space-y-4 text-gray-700 leading-relaxed">
        <p>{t('terms.liability.p1')}</p>
        <p>{t('terms.liability.intro')}</p>
        <div className="p-4 rounded-lg border-l-4" style={{ borderColor: theme.UvibesBlue, backgroundColor: theme.UvibesBlue + '20' }}>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-gray-500">•</span>
              <span>{t('terms.liability.item_1')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-gray-500">•</span>
              <span>{t('terms.liability.item_2')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-gray-500">•</span>
              <span>{t('terms.liability.item_3')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-gray-500">•</span>
              <span>{t('terms.liability.item_4')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-gray-500">•</span>
              <span>{t('terms.liability.item_5')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-gray-500">•</span>
              <span>{t('terms.liability.item_6')}</span>
            </li>
          </ul>
        </div>
        <p>{t('terms.liability.indirect')}</p>
      </div>
    </section>
  );
};

const ModificationsCGU: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle number="10" title={t('terms.changes.title')} emoji="📝" />
      <div className="space-y-4 text-gray-700 leading-relaxed">
        <p>
          {t('terms.changes.p1')}
        </p>
        <p>
          {t('terms.changes.p2')}
        </p>
      </div>
    </section>
  );
};

const Resiliation: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle number="11" title={t('terms.termination.title')} emoji="🚪" />
      <div className="text-gray-700 leading-relaxed">
        <p>
          {t('terms.termination.p1')}
        </p>
      </div>
    </section>
  );
};

const DroitApplicable: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-6">
      <SectionTitle number="12" title={t('terms.law.title')} emoji="🏛️" />
      <div className="text-gray-700 leading-relaxed">
        <p>
          {t('terms.law.p1')}
        </p>
      </div>
    </section>
  );
};

const Footer: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="space-y-6 py-8">
      <div className="p-6 rounded-lg text-center" style={{ backgroundColor: theme.UvibesPink + '20', borderColor: theme.UvibesPink }}>
        <p className="text-lg text-gray-700 mb-4">
          <Trans
            i18nKey="terms.footer.thanks"
            components={{ brand: <span className="font-semibold" style={{ color: theme.UvibesPink }} /> }}
          />
        </p>
        <div className="text-2xl mb-4">🙏</div>
      </div>

      <div className="text-center text-gray-600 border-t pt-6">
        <p className="font-medium">
          {t('legal.signed')}
        </p>
      </div>
    </div>
  );
};

interface SectionTitleProps {
  number?: string;
  title: string;
  emoji: string;
}

const SectionTitle: React.FC<SectionTitleProps> = ({ number, title, emoji }) => {
  return (
    <div className="border-l-4 pl-4" style={{ borderColor: theme.UvibesPink }}>
      <h2 className="text-2xl font-bold flex items-center gap-3 text-black">
        {number && <span style={{ color: theme.UvibesPink }}>{number}.</span>}
        <span className="text-2xl">{emoji}</span>
        <span>{title}</span>
      </h2>
    </div>
  );
};

interface ConsequenceCardProps {
  emoji: string;
  title: string;
  description: string;
}

const ConsequenceCard: React.FC<ConsequenceCardProps> = ({ emoji, title, description }) => {
  return (
    <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-yellow-200">
      <span className="text-2xl">{emoji}</span>
      <div>
        <h5 className="font-medium text-gray-800 mb-1">{title}</h5>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
  );
};

export default CGUUvibes;
